import React, { Fragment, useMemo } from 'react'
import useSWR from 'swr'
import { useTranslation } from 'react-i18next'
import { faGraduationCap } from '@fortawesome/pro-solid-svg-icons'
import Loader from 'components/ui/loader/Loader'
import myCoursesCategories from 'api/courses/MyCoursesCategories'
import { paths } from 'routes/paths'
import { EmptyStateSection } from 'components/ui/empty-states/empty-state-section'
import { Program } from 'api/courses/types'
import { Courses } from './Courses'

const ByCategories: React.FC<{
  search: string
}> = ({ search }) => {
  const { t } = useTranslation()

  const { data, isLoading: byCategoriesLoading } = useSWR(
    { key: 'myCoursesCategories', search: search },
    async (arg) => myCoursesCategories({ search: arg.search })
  )

  const learningPrograms = useMemo(() => data?.learningPrograms || [], [data])
  const folders = useMemo(() => data?.folders || [], [data])
  const premieres = useMemo(() => data?.premieres, [data])

  const total =
    folders.length +
    (premieres?.courses.length || 0) +
    (learningPrograms.length || 0)

  const replacedCourses = (program: Program) => {
    return program.sets.flatMap((set) => set.courses)
  }

  return (
    <Fragment>
      {byCategoriesLoading ? (
        <Loader color />
      ) : (
        <>
          {learningPrograms.map((program: Program) => (
            <Courses
              key={program.id}
              name={program.name}
              courses={replacedCourses(program)}
              totalCourses={replacedCourses(program).length}
              href={paths.learningPath.details(program.id)}
              tag={{
                name: t('learningPaths.cards.tags.default'),
                icon: faGraduationCap,
              }}
            />
          ))}
          {!!premieres?.courses.length && (
            <Courses
              name={t('viewAll.title.premieres')}
              courses={premieres.courses}
              totalCourses={premieres.count}
              href={paths.myCourses.premieres}
            />
          )}
          {folders.map((folder) => (
            <Courses
              key={folder.id}
              name={folder.name}
              courses={folder.courses}
              totalCourses={folder.coursesCount || 0}
              href={paths.myCourses.folder.details(
                folder.id,
                folder.name,
                'origin=folders'
              )}
            />
          ))}
          {!total && (
            <div className="w-100 d-flex align-center justify-center h-100 mt-5">
              <EmptyStateSection
                variant="categories"
                text={t(
                  'home.lmsIndex.lmsMyCourses.emptyStates.byCategory.section.text'
                )}
                link={{
                  href: paths.library.root,
                  text: t(
                    'home.lmsIndex.lmsMyCourses.emptyStates.byCategory.section.link'
                  ),
                }}
              />
            </div>
          )}
        </>
      )}
    </Fragment>
  )
}

export default ByCategories
